import React from 'react';

import { loadAndCreateAnalyses as loadAndCreateAnalysesProcedure } from './procedures';
import { AnalysisConfig } from '../../helpers/analyses-config';
import { ResourceAnalysis } from '../../types/analysis';

type AnalysesContextTypes<T extends string, TResource = any> = {
  loadAndCreateAnalyses: (_isSharedResource: boolean, _resource?: TResource) => Promise<T[]>;
  resourceType: ResourceAnalysis['item_type'];
  resourceId: number;
  config: AnalysisConfig<T, TResource>;
};

const AnalysesContext = React.createContext<AnalysesContextTypes<any> | null>(null);

type ProviderProps<T extends string> = {
  config: AnalysisConfig<T>;
  resourceId: number;
  resourceType: ResourceAnalysis['item_type'];
  children: React.ReactNode;
};

function Provider<T extends string, TResource = any>({ config, resourceId, resourceType, children }: ProviderProps<T>) {
  const loadAndCreateAnalyses = React.useCallback(
    (isSharedResource: boolean, resource?: TResource) =>
      loadAndCreateAnalysesProcedure(config, resourceId, resourceType, isSharedResource, resource),
    [config, resourceId, resourceType]
  );

  return (
    <AnalysesContext.Provider
      value={{
        loadAndCreateAnalyses,
        resourceType,
        resourceId,
        config,
      }}
    >
      {children}
    </AnalysesContext.Provider>
  );
}

const AnalysesContextProvider = Provider;

const useAnalysesContext = <T extends string, TResource = any>(): AnalysesContextTypes<T, TResource> => {
  const context = React.useContext(AnalysesContext);
  if (!context) {
    throw new Error('useAnalysesContext must be used within an AnalysesContextProvider');
  }
  return context as AnalysesContextTypes<T, TResource>;
};

export { AnalysesContext };
export { AnalysesContextProvider };
export { useAnalysesContext };

export type { AnalysisWrapperRenderProps } from './analysis-wrapper';
export { AnalysisWrapper } from './analysis-wrapper';
